import { DestroyRef, inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import localForage from 'localforage';
import { first, from } from 'rxjs';
import { ServiceAuthService } from 'src/app/auth/services/service-auth/service-auth.service';
import { DATABASE_NAME, DEFAULT_LANG } from 'src/app/config/app.config';
import { localForageConfig } from 'src/app/config/localforage.config';
import { DeviceSocketHandlerService } from 'src/app/core/entities/device/device-socket-handler.service';
import { DeviceStatusService } from 'src/app/core/entities/device/device-status.service';
import { SignatureService } from 'src/app/core/entities/service/signature.service';
import { STORE_DATABASE } from 'src/app/core/enums/storage/store-database.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { AppConfigService } from 'src/app/core/services/app-config/app-config.service';
import { CleanerService } from 'src/app/core/services/cleaner/cleaner.service';
import { DataLoaderService } from 'src/app/core/services/data-loader/data-loader.service';
import { NavigationService } from 'src/app/core/services/navigation/navigation.service';
import { NetworkConnectionService } from 'src/app/core/services/network-connection/network-connection.service';
import { SocketConnectionHandlerService } from 'src/app/core/services/socket/socket-connection-handler.service';
import { SocketRoomHandlerService } from 'src/app/core/services/socket/socket-room-handler.service';
import { AccountTrackerService } from 'src/app/core/services/tracking/trackers/account-tracker.service';
import { PaymentTrackerService } from 'src/app/core/services/tracking/trackers/payment-tracker.service';
import { DynamicScriptLoaderService } from '../dynamic-script-loader/dynamic-script-loader.service';
import { IScriptStatus } from '../dynamic-script-loader/interface/script.interface';
import { hotjarScript } from '../dynamic-script-loader/scripts/hotjar.script';
import { FeatureFlagAdapter } from '../../infrastructure/adapters/feature-flag.adapter';
import { LayoutService } from '../layout/layout.service';
import { PwaServiceWorkersService } from '../pwa/pwa-service-workers.service';
import { StorageService } from '../storage/storage.service';
import { WaiterStatusService } from '@core/entities/waiter/waiter-status.service';
import { LocationStatusService } from '@core/entities/location/location-status.service';
import { DrawerStatusService } from '@core/entities/drawer/drawer-status.service';

declare global {
  interface Window {
    fingerprint: string;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly translateService = inject(TranslateService);
  private readonly dynamicScriptLoader = inject(DynamicScriptLoaderService);
  private readonly logger = inject(Logger);
  private readonly networkConnectionService = inject(NetworkConnectionService);
  public readonly socketRoomHandler = inject(SocketRoomHandlerService);
  public readonly socketConnectionHandler = inject(
    SocketConnectionHandlerService
  );
  public readonly pwaServiceWorkers = inject(PwaServiceWorkersService);
  public readonly navigationService = inject(NavigationService);
  public readonly signatureService = inject(SignatureService);
  public readonly serviceAuthService = inject(ServiceAuthService);
  public readonly deviceSocketHandlerService = inject(
    DeviceSocketHandlerService
  );
  public readonly dataLoaderService = inject(DataLoaderService);
  public readonly cleanerService = inject(CleanerService);
  public readonly deviceStatusService = inject(DeviceStatusService);
  public readonly waiterStatusService = inject(WaiterStatusService);
  public readonly locationStatusService = inject(LocationStatusService);
  public readonly drawerStatusService = inject(DrawerStatusService);
  public readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly storageService = inject(StorageService);
  public readonly layoutService = inject(LayoutService);
  public readonly appConfigService = inject(AppConfigService);

  public readonly accountTrackerService = inject(AccountTrackerService);
  public readonly paymentTrackerService = inject(PaymentTrackerService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    from(this.initializeDatabases()).pipe(
      first(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.setLanguage();
      this.loadTheme();
      this.loadScripts();
      this.networkConnectionService.init();
    });
  }

  /**
   * Creates an instance of current data in the local storage.
   */
  private getCurrentDataInstance(): void {
    this.storageService.getInstance(DATABASE_NAME, STORE_DATABASE.current);
  }

  /**
   * Creates an instance of current config in the local storage.
   */
  private getCurrentConfigInstance(): void {
    this.storageService.getInstance(DATABASE_NAME, STORE_DATABASE.config);
  }

  /**
   * Loads the required external scripts.
   */
  private loadScripts(): void {
    this.loadHotjarScript();
  }

  /**
   * Sets the default language to DEFAULT_LANG and uses it.
   */
  private setLanguage(): void {
    this.translateService.setDefaultLang(DEFAULT_LANG);
    this.translateService.use(DEFAULT_LANG);
  }

  /**
   * Configures localForage with the specified configuration.
   */
  private async initializeDatabases(): Promise<void> {
    localForage.config(localForageConfig);
    await localForage
      .ready()
      .then(() =>
        this.logger.debug(
          '[AppInitializer][initializeDatabases] Local Forage ready'
        )
      );
    this.getCurrentDataInstance();
    this.getCurrentConfigInstance();
  }

  /**
   * Dynamically loads the Hotjar script.
   */
  private loadHotjarScript(): void {
    this.dynamicScriptLoader
      .loadScript(hotjarScript.name)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status: IScriptStatus) => {
          this.logger.debug(status.status);
        },
        error: (status: IScriptStatus) => {
          this.logger.debug(status.status);
        },
      });
  }

  private loadTheme(): void {
    this.layoutService.loadTheme();
  }
}
